import React, { useState, useEffect, useRef, useMemo } from 'react';


/**
 * FolderManagement Component
 * * A complete solution for managing multilingual folders with a custom
 * multi-select dropdown for "Duas" (Prayers).
 * * Features:
 * - Bootstrap 5 Styling
 * - FontAwesome Icons
 * - CRUD Operations (Create, Read, Update, Delete)
 * - Multilingual Inputs (English, Hindi, Urdu (RTL), Gujarati, Transliteration)
 * - Advanced Multi-Select with Search & Select All
 */


// --- Mock Data for Duas ---
const MOCK_DUAS = [
  { id: 1, name: "Dua for Knowledge (Rabbi Zidni Ilma)" },
  { id: 2, name: "Dua for Parents (Rabbir Hamhuma)" },
  { id: 3, name: "Dua for Forgiveness (Rabbana Zalamna)" },
  { id: 4, name: "Dua for Patience (Rabbana Afrigh)" },
  { id: 5, name: "Dua for Health (Allahumma Afini)" },
  { id: 6, name: "Dua for Travel (Subhanalladhi)" },
  { id: 7, name: "Dua for Eating (Bismillah)" },
  { id: 8, name: "Dua after Eating (Alhamdulillah)" },
  { id: 9, name: "Dua for Sleeping (Allahumma Bismika)" },
  { id: 10, name: "Dua for Waking Up (Alhamdulillahilladhi)" },
  { id: 11, name: "Ayatul Kursi" },
  { id: 12, name: "Surah Al-Fatiha" }
];


// --- Initial Form State ---
const INITIAL_FORM_STATE = {
  id: null,
  nameEn: '',
  nameHi: '',
  nameTrans: '',
  nameUr: '',
  nameGu: '',
  selectedDuas: [] // Array of IDs
};


export default function FolderManagement() {
  // --- State Management ---
  const [folders, setFolders] = useState([
    {
      id: 1,
      nameEn: 'Morning Adhkar',
      nameHi: 'सुबह के अज़कार',
      nameTrans: 'Subah ke Azkar',
      nameUr: 'صبح کے اذکار',
      nameGu: 'સવારના અઝકાર',
      selectedDuas: [1, 5, 11]
    }
  ]);
 
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
 
  // --- Effect to Inject Bootstrap & FontAwesome ---
  useEffect(() => {
    // Inject Bootstrap 5 CSS
    const bsLink = document.createElement('link');
    bsLink.rel = 'stylesheet';
    bsLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css';
    document.head.appendChild(bsLink);


    // Inject FontAwesome
    const faLink = document.createElement('link');
    faLink.rel = 'stylesheet';
    faLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    document.head.appendChild(faLink);


    // Cleanup not strictly necessary for single-file demos, but good practice
    return () => {
      // document.head.removeChild(bsLink);
      // document.head.removeChild(faLink);
    };
  }, []);


  // --- Handlers ---


  const handleOpenCreate = () => {
    setFormData(INITIAL_FORM_STATE);
    setIsEditMode(false);
    setShowModal(true);
  };


  const handleOpenEdit = (folder) => {
    setFormData({ ...folder });
    setIsEditMode(true);
    setShowModal(true);
  };


  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this folder?')) {
      setFolders(prev => prev.filter(f => f.id !== id));
    }
  };


  const handleSave = () => {
    // Basic Validation
    if (!formData.nameEn.trim()) {
      alert("English Folder Name is required.");
      return;
    }


    if (isEditMode) {
      setFolders(prev => prev.map(f => f.id === formData.id ? formData : f));
    } else {
      const newFolder = {
        ...formData,
        id: Date.now() // Simple ID generation
      };
      setFolders(prev => [...prev, newFolder]);
    }
    setShowModal(false);
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  return (
    <div className="container mt-5 p-4 bg-light rounded shadow-sm">
      {/* Custom Theme Styles */}
      <style>
        {`
          :root {
            --bs-primary: #009587; /* Deeniyat Brand Teal */
            --bs-primary-rgb: 0, 149, 135;
            --brand-gold: #f4c01e;
          }
          .btn-primary {
            background-color: var(--bs-primary);
            border-color: var(--bs-primary);
          }
          .btn-primary:hover {
            background-color: #00796b;
            border-color: #00796b;
          }
          .text-primary {
            color: var(--bs-primary) !important;
          }
          .bg-primary {
            background-color: var(--bs-primary) !important;
          }
          .brand-border-top {
            border-top: 4px solid var(--brand-gold);
          }
          .modal-header {
             border-bottom: 3px solid var(--brand-gold);
          }
        `}
      </style>


      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom brand-border-top bg-white p-3 rounded">
        <div className="d-flex align-items-center">
          <img
            src="https://uat.deeniyat.com/deeniyat/wp-content/uploads/2020/08/Deeniyat_logo@4x.png"
            alt="Deeniyat Logo"
            style={{ height: '50px', marginRight: '15px' }}
          />
          <div>
            <h2 className="text-primary mb-0 fw-bold" style={{ letterSpacing: '-0.5px' }}>Folder Management</h2>
            <p className="text-muted small mb-0">Manage your multilingual Dua collections</p>
          </div>
        </div>
        <button className="btn btn-primary shadow-sm" onClick={handleOpenCreate}>
          <i className="fas fa-plus me-2"></i> Create New Folder
        </button>
      </div>


      {/* Folder List Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col" className="ps-4">Folder Name (English)</th>
                  <th scope="col">Urdu Name</th>
                  <th scope="col">Duas Count</th>
                  <th scope="col" className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {folders.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted">
                      <i className="fas fa-folder-open fa-3x mb-3 opacity-25"></i>
                      <p>No folders found. Create one to get started.</p>
                    </td>
                  </tr>
                ) : (
                  folders.map(folder => (
                    <tr key={folder.id}>
                      <td className="ps-4 fw-bold text-dark">{folder.nameEn}</td>
                      <td className="font-urdu" style={{ fontFamily: 'Tahoma, Arial', direction: 'rtl' }}>
                        {folder.nameUr || '-'}
                      </td>
                      <td>
                        <span className="badge bg-info text-dark rounded-pill">
                          {folder.selectedDuas.length} Duas
                        </span>
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          title="View"
                          onClick={() => alert(`Viewing details for: ${folder.nameEn}`)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          title="Edit"
                          onClick={() => handleOpenEdit(folder)}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => handleDelete(folder.id)}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      {/* Bootstrap Modal (Custom Implementation for React) */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title fw-bold">
                    {isEditMode ? <><i className="fas fa-edit me-2"></i> Edit Folder</> : <><i className="fas fa-folder-plus me-2"></i> Create New Folder</>}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                </div>
                <div className="modal-body">
                  <form>
                    {/* Multilingual Inputs Grid */}
                    <div className="row g-3">
                      {/* English */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Folder Name (English) <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Morning Adhkar"
                          value={formData.nameEn}
                          onChange={(e) => handleInputChange('nameEn', e.target.value)}
                        />
                      </div>


                      {/* Hindi */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Folder Name (Hindi)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. सुबह के अज़कार"
                          value={formData.nameHi}
                          onChange={(e) => handleInputChange('nameHi', e.target.value)}
                        />
                      </div>


                      {/* Transliteration */}
                      <div className="col-12">
                        <label className="form-label fw-semibold">Folder Name (Transliteration)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Subah ke Azkar"
                          value={formData.nameTrans}
                          onChange={(e) => handleInputChange('nameTrans', e.target.value)}
                        />
                      </div>


                      {/* Urdu (RTL) */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold d-flex justify-content-between">
                          Folder Name (Urdu)
                          <span className="badge bg-secondary">RTL</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-end"
                          dir="rtl"
                          placeholder="e.g. صبح کے اذکار"
                          value={formData.nameUr}
                          onChange={(e) => handleInputChange('nameUr', e.target.value)}
                          style={{ fontFamily: 'Tahoma, Arial' }}
                        />
                      </div>


                      {/* Gujarati */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Folder Name (Gujarati)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. સવારના અઝકાર"
                          value={formData.nameGu}
                          onChange={(e) => handleInputChange('nameGu', e.target.value)}
                        />
                      </div>
                    </div>


                    <hr className="my-4" />


                    {/* Dua Multi-Select Section */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold mb-2">Assign Duas to Folder</label>
                      <MultiSelectDua
                        options={MOCK_DUAS}
                        selectedIds={formData.selectedDuas}
                        onChange={(ids) => handleInputChange('selectedDuas', ids)}
                      />
                    </div>
                  </form>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary px-4" onClick={handleSave}>
                    <i className="fas fa-save me-2"></i>
                    {isEditMode ? 'Update Folder' : 'Create Folder'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/**
 * Custom Multi-Select Dropdown for Duas
 * Handles searching, filtering, selecting all, and specific item selection.
 */
function MultiSelectDua({ options, selectedIds, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);


  // Filter options based on search
  const filteredOptions = useMemo(() => {
    return options.filter(opt =>
      opt.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);


  // Handlers
  const toggleDropdown = () => setIsOpen(!isOpen);


  const handleCheckboxChange = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(itemId => itemId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all currently visible (filtered) options
      const visibleIds = filteredOptions.map(o => o.id);
      // Merge with existing selection to avoid duplicates
      const newSelection = [...new Set([...selectedIds, ...visibleIds])];
      onChange(newSelection);
    } else {
      // Deselect all currently visible options
      const visibleIds = filteredOptions.map(o => o.id);
      onChange(selectedIds.filter(id => !visibleIds.includes(id)));
    }
  };


  // Derived state for "Select All" checkbox
  const areAllVisibleSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedIds.includes(opt.id));


  return (
    <div className="position-relative" ref={dropdownRef}>
      {/* Dropdown Toggle Button */}
      <button
        type="button"
        className="form-select text-start d-flex justify-content-between align-items-center"
        onClick={toggleDropdown}
      >
        <span className="text-truncate">
          {selectedIds.length === 0
            ? "Select Duas..."
            : `${selectedIds.length} Duas Selected`}
        </span>
      </button>


      {/* Dropdown Menu Content */}
      {isOpen && (
        <div className="card position-absolute w-100 shadow mt-1 border-primary" style={{ zIndex: 1050, maxHeight: '300px' }}>
         
          {/* Search Box Header */}
          <div className="card-header bg-white p-2">
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Search Duas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
           
            {/* Select All Checkbox */}
            <div className="form-check border-bottom pb-2 mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="selectAll"
                checked={areAllVisibleSelected}
                onChange={handleSelectAll}
                disabled={filteredOptions.length === 0}
              />
              <label className="form-check-label small fw-bold text-primary w-100" htmlFor="selectAll" style={{cursor: 'pointer'}}>
                {areAllVisibleSelected ? "Deselect All Visible" : "Select All Visible"}
              </label>
            </div>
          </div>


          {/* List Items */}
          <div className="card-body p-0 overflow-auto" style={{ maxHeight: '200px' }}>
            {filteredOptions.length === 0 ? (
              <div className="text-center p-3 text-muted small">No duas found.</div>
            ) : (
              <ul className="list-group list-group-flush">
                {filteredOptions.map(opt => (
                  <li key={opt.id} className="list-group-item list-group-item-action p-2">
                    <div className="form-check m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`dua-${opt.id}`}
                        checked={selectedIds.includes(opt.id)}
                        onChange={() => handleCheckboxChange(opt.id)}
                      />
                      <label className="form-check-label w-100 text-truncate" htmlFor={`dua-${opt.id}`} style={{cursor: 'pointer'}}>
                        {opt.name}
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
         
          {/* Footer Status */}
          <div className="card-footer bg-light p-1 text-center">
            <small className="text-primary fw-semibold" style={{ fontSize: '0.75rem' }}>
              {selectedIds.length} total selected
            </small>
          </div>
        </div>
      )}
    </div>
  );
}

